'use client';

import { useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import SwipeDeck from './SwipeDeck';
import { tryCtaScrollToFinal } from '../lib/tryNavigation';

/**
 * SCENE 8: Свайп-разбор. Чисто, без cinematic-фона.
 * Слева — текст и кнопка, справа — интерактивный SwipeDeck.
 * Маскот-иллюстрация убрана: колода и so есть полное визуальное выражение
 * механики (drag + цветные label-подсказки), а две демки рядом конкурируют.
 */
export default function Scene08Swipe() {
  const reduced = useReducedMotion() ?? false;

  const goToMagic = useCallback(() => {
    document.getElementById('scene-magic')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const goToFinal = useCallback(() => {
    tryCtaScrollToFinal();
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#f3fcf0] flex items-center">
      <BgOrbs variant="warm" />

      <div className="relative max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 lg:gap-16 items-center px-5 sm:px-8 py-20 lg:py-24">
        {/* Левая — текст */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-start gap-6 lg:gap-7"
        >
          <h2
            className="font-display font-bold text-[#161d16] leading-[0.98] tracking-[-0.02em]"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
          >
            Разберём<br />
            <span className="text-[#b7102a]">как в Тиндере.</span>
          </h2>
          <p className="font-body text-[#4a5a49] text-base sm:text-lg leading-relaxed max-w-md">
            Влево — удалить. Вправо — закрепить. Вверх — в Лучшее. Одна сессия — и накопленное превращается в понятные стопки.
          </p>
          <div className="flex flex-wrap gap-2 text-[#161d16] font-body text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#dce5d9]">← 🗑 удалить</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#dce5d9]">↑ ❤ в Лучшее</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#dce5d9]">📌 закрепить →</span>
          </div>
          <div className="flex flex-col items-start gap-5 mt-1">
            <button
              type="button"
              onClick={goToFinal}
              className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
              style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
            >
              <span>Попробовать сейчас</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goToMagic}
              className="group inline-flex items-center gap-2 text-[#4a5a49] hover:text-[#161d16] font-body text-sm font-medium transition-colors"
            >
              <span>А в один клик?</span>
              <motion.svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                animate={reduced ? {} : { y: [0, 4, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </motion.svg>
            </button>
          </div>
        </motion.div>

        {/* Правая — интерактивная колода */}
        <motion.div
          initial={reduced ? false : { opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex items-center justify-center"
        >
          <SwipeDeck />
        </motion.div>
      </div>
    </section>
  );
}
